import React from 'react';
import { Container, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import { PENDING_CHECKOUT_KEY, CHECKOUT_MESSAGE_TYPE } from './checkoutConstants';
import { stashPendingAddress } from '../../utils/pendingAddress';
import PageHeading from '../PageHeading';

class CheckoutSuccess extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      order: null,
      pollCount: 0,
    };
    this.pollTimer = null;
  }

  componentDidMount() {
    this.notifyOpener();
    this.fetchStatus();
  }

  componentWillUnmount() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }

  getOrderId = () => {
    const params = new URLSearchParams(this.props.location.search);
    return params.get('order');
  };

  notifyOpener = () => {
    const orderId = this.getOrderId();
    if (!orderId) return;
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: CHECKOUT_MESSAGE_TYPE, orderId },
          window.location.origin
        );
      }
    } catch (err) {
      // Cross-origin or closed opener — parent poll/close detection will handle it.
    }
  };

  clearPendingMarker = () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
    }
  };

  fetchStatus = async () => {
    const orderId = this.getOrderId();
    if (!orderId) {
      this.setState({
        loading: false,
        error: 'Missing order reference.',
      });
      return;
    }

    try {
      const res = await axios.get(`/api/checkout/${orderId}`, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        params: { _: Date.now() },
      });
      const order = res.data;
      if (order.status === 'paid' || order.status === 'canceled') {
        this.clearPendingMarker();
      }
      if (order.status === 'paid') {
        this.notifyOpener();
        if (!this.props.isLoggedIn && order.shippingAddress) {
          stashPendingAddress({
            ...order.shippingAddress,
            fullName:
              order.shippingAddress.fullName ||
              [order.shippingAddress.firstName, order.shippingAddress.lastName]
                .filter(Boolean)
                .join(' '),
          });
        }
      }
      this.setState({ order, loading: false, error: null });

      if (order.status === 'pending_payment' && this.state.pollCount < 15) {
        this.pollTimer = setTimeout(() => {
          this.setState(
            (prev) => ({ pollCount: prev.pollCount + 1, loading: true }),
            this.fetchStatus
          );
        }, 2000);
      }
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        'Unable to verify payment status.';
      this.setState({ loading: false, error: message });
    }
  };

  renderBody() {
    const { loading, error, order } = this.state;
    const { isLoggedIn } = this.props;

    if (loading && !order) {
      return (
        <div className="text-center pad2">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Confirming your payment…</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger">
          {error}{' '}
          <Link to="/shopping-cart">Return to bag</Link>
        </Alert>
      );
    }

    if (order.status === 'paid') {
      const formatCents = (cents) =>
        cents == null ? null : `$${(Number(cents) / 100).toFixed(2)}`;
      return (
        <>
          <Alert variant="success">Payment received. Thank you!</Alert>
          <Card.Text>
            Order <code>{order.orderId}</code>
          </Card.Text>
          <div className="cart-order-summary">
            {order.subtotalCents != null && (
              <div className="cart-order-summary__row">
                <span>Subtotal</span>
                <span>{formatCents(order.subtotalCents)}</span>
              </div>
            )}
            {order.shippingCents != null && (
              <div className="cart-order-summary__row">
                <span>Shipping</span>
                <span>{formatCents(order.shippingCents)}</span>
              </div>
            )}
            {order.taxCents != null && (
              <div className="cart-order-summary__row">
                <span>Sales Tax</span>
                <span>{formatCents(order.taxCents)}</span>
              </div>
            )}
            <div className="cart-order-summary__row cart-order-summary__row--total">
              <span>Total</span>
              <span>{formatCents(order.totalCents)}</span>
            </div>
          </div>
          <ul>
            {(order.lineItems || []).map((item) => (
              <li key={`${item.productId}-${item.name}`}>
                {item.name} × {item.quantity}
              </li>
            ))}
          </ul>
          {!isLoggedIn && (
            <div className="cart-create-account-prompt checkout-success-prompt">
              <p>
                Create an account to save your shipping address on file and see
                past orders.
              </p>
              <Button as={Link} to="/signup" variant="outline-success" size="sm">
                Create account
              </Button>
            </div>
          )}
          <Button as={Link} to="/shop-all" variant="success">
            Continue shopping
          </Button>
        </>
      );
    }

    if (order.status === 'pending_payment') {
      return (
        <>
          <Alert variant="warning">
            We have not confirmed payment yet. If you completed checkout, this
            page will update shortly.
          </Alert>
          {loading && <Spinner animation="border" size="sm" className="me-2" />}
          <Button variant="outline-secondary" onClick={this.fetchStatus}>
            Refresh status
          </Button>{' '}
          <Button as={Link} to="/shopping-cart" variant="link">
            Back to bag
          </Button>
        </>
      );
    }

    return (
      <>
        <Alert variant="secondary">
          This order is {order.status.replace('_', ' ')}.
        </Alert>
        <Button as={Link} to="/shopping-cart" variant="success">
          Return to bag
        </Button>
      </>
    );
  }

  render() {
    return (
      <Container className="pad-t R-clm">
        <PageHeading as="h2" icon="check">
          Checkout
        </PageHeading>
        <Card border="light" className="Form pad2">
          {this.renderBody()}
        </Card>
      </Container>
    );
  }
}

const mapState = (state) => ({
  isLoggedIn: !!state.user.id,
});

export default connect(mapState)(CheckoutSuccess);

CheckoutSuccess.propTypes = {
  isLoggedIn: PropTypes.bool,
  location: PropTypes.object,
};
